<template>
  <div
    class="app-card-glass rounded-b-lg shadow-sm pb-1 px-2 py-2 sm:px-3 border-t border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)] flex-shrink-0"
  >
    <div v-if="showAudioRecorder" class="flex items-end gap-2">
      <div class="min-w-0 flex-1">
        <AudioRecorder @send-audio="$emit('send-audio', $event)" @cancel="$emit('cancel-audio')" />
      </div>
    </div>

    <div
      v-else-if="isClosedByTimeout"
      class="flex items-start justify-between gap-2 px-2.5 py-2.5 sm:items-center sm:gap-3 sm:px-3 rounded-lg"
    >
      <div class="flex min-w-0 items-start gap-2 sm:items-center">
        <svg
          class="w-4 h-4 text-[var(--p-amber-600)] dark:text-[var(--p-amber-400)] flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p
          class="min-w-0 text-xs text-[var(--p-amber-800)] dark:text-[var(--p-amber-200)] leading-snug break-words"
        >
          <template v-if="timeoutCampaignName">
            {{ t('whatsapp.actions.timeout_closed_campaign', { name: timeoutCampaignName }) }}
          </template>
          <template v-else-if="timeoutBotId">
            {{ t('whatsapp.actions.timeout_redirected_to_bot') }}
          </template>
          <template v-else>
            {{ t('whatsapp.actions.timeout_closed_finished') }}
          </template>
        </p>
      </div>
      <Button
        text
        rounded
        size="small"
        severity="secondary"
        class="flex-shrink-0 p-0.5!"
        @click="$emit('close-chat')"
        v-tooltip.top="t('whatsapp.actions.close_chat')"
      >
        <template #default>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </template>
      </Button>
    </div>

    <div
      v-else-if="releasedByTemplateReason"
      class="flex items-start justify-between gap-2 px-2.5 py-2.5 sm:items-center sm:gap-3 sm:px-3 bg-[var(--p-blue-50)] dark:bg-[var(--p-blue-900)]/20 border border-[var(--p-blue-300)] dark:border-[var(--p-blue-700)] rounded-lg"
    >
      <div class="flex min-w-0 items-start gap-2 sm:items-center">
        <svg
          class="w-4 h-4 text-[var(--p-blue-600)] dark:text-[var(--p-blue-400)] flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p
          class="min-w-0 text-xs text-[var(--p-blue-800)] dark:text-[var(--p-blue-200)] leading-snug break-words"
        >
          {{ releaseBannerText }}
        </p>
      </div>
      <Button
        text
        rounded
        size="small"
        severity="secondary"
        class="flex-shrink-0 p-0.5!"
        @click="$emit('close-bulk-template-chat')"
        v-tooltip.top="t('whatsapp.actions.close_chat')"
      >
        <template #default>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </template>
      </Button>
    </div>

    <div v-else-if="isPendingChat" class="flex flex-wrap items-center justify-center gap-1">
      <Button
        rounded
        class="flex items-center gap-2 !bg-[var(--p-primary-color)]/10 text-[var(--p-primary-color)] dark:text-white hover:!bg-[var(--p-primary-300)]/50 dark:bg-[var(--p-primary-color)]/20  dark:hover:!bg-[var(--p-primary-900)]/80"
        severity="secondary"
        size="small"
        :loading="isTakingChat"
        :disabled="isTakingChat"
        @click="handleTakeChat"
      >
        <Check :size="18" class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]" />
        <span class="text-sm text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]">
          {{ isTakingChat ? t('whatsapp.actions.taking_chat') : t('whatsapp.actions.take_chat') }}
        </span>
      </Button>

      <!-- Un chat EXPIRE en cola ya no va a recibir nada del cliente: cerrarlo no
           exige tomarlo antes, asi que el boton de finalizar acompana al de tomar
           en vez de aparecer solo cuando el chat es propio. -->
      <Button
        v-if="isOutside24HourWindow"
        text
        rounded
        size="small"
        severity="secondary"
        class="flex items-center gap-2"
        v-tooltip.top="t('whatsapp.actions.finish_chat')"
        @click="$emit('end-chat')"
      >
        <IconHand width="18" height="18" class="text-[var(--p-primary-color)]" />
        <span class="text-sm text-[var(--p-primary-color)]">
          {{ t('whatsapp.actions.finish_chat') }}
        </span>
      </Button>
    </div>

    <div
      v-else-if="isOutside24HourWindow || isAwaitingClientReply"
      class="grid grid-cols-1 items-center gap-2 md:grid-cols-[minmax(0,1fr)_auto] 2xl:grid-cols-[auto_minmax(0,1fr)_auto]"
    >
      <div
        v-if="!isAwaitingClientReply"
        class="order-2 flex w-full min-w-0 justify-start md:order-2 md:w-auto 2xl:order-1"
      >
        <Button
          text
          rounded
          severity="secondary"
          class="w-full min-w-0 shrink-0 justify-center gap-2 md:w-auto md:justify-start dark:hover:!bg-[var(--p-primary-900)]/70"
          @click="$emit('send-template')"
        >
          <IconTemplate width="20" height="20" class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]" />
          <span class="min-w-0 truncate text-sm text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]">
            {{ t('whatsapp.actions.reactivate_chat') }}
          </span>
        </Button>
      </div>
      <div
        class="order-1 col-span-1 flex min-w-0 items-start justify-center gap-2 rounded-lg px-2.5 py-2 md:col-span-2 md:items-center 2xl:order-2 2xl:col-span-1 2xl:px-3"
      >
        <svg
          class="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p
          class="min-w-0 text-center text-xs leading-snug text-orange-800 dark:text-orange-200 lg:truncate"
        >
          {{
            isAwaitingClientReply
              ? t('whatsapp.actions.awaiting_client_reply')
              : t('whatsapp.actions.outside_24_hour_window')
          }}
        </p>
      </div>

      <div
        class="order-3 flex w-full min-w-0 flex-col items-stretch justify-end gap-1 md:w-auto md:flex-row md:items-center md:gap-1 2xl:order-3"
      >
        <Button
          v-if="showTransferButton"
          text
          rounded
          severity="secondary"
          class="w-full min-w-0 flex-1 justify-center gap-2 md:w-auto md:flex-none md:justify-end dark:hover:!bg-[var(--p-primary-900)]/70"
          v-tooltip.top="`${t('whatsapp.actions.chat_transfer')} (Alt+Shift+T)`"
          @click="$emit('transfer-chat')"
        >
          <IconTransfer width="16" height="16" class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]" />
          <span class="min-w-0 truncate text-sm text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]">
            {{ t('whatsapp.actions.chat_transfer') }}
          </span>
        </Button>

        <Button
          v-if="isOutside24HourWindow"
          text
          rounded
          severity="secondary"
          class="w-full min-w-0 flex-1 justify-center gap-2 md:w-auto md:flex-none md:justify-end dark:hover:!bg-[var(--p-primary-900)]/70"
          v-tooltip.top="t('whatsapp.actions.finish_chat')"
          @click="$emit('end-chat')"
        >
          <IconHand width="18" height="18" class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]" />
          <span class="min-w-0 truncate text-sm text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]">
            {{ t('whatsapp.actions.finish_chat') }}
          </span>
        </Button>
      </div>
    </div>

    <div v-else class="flex flex-col gap-2">
      <div class="min-w-0 w-full">
        <MessageInput
          ref="messageInputRef"
          :is-signature-enabled="isSignatureEnabled"
          :is-translation-enabled="isTranslationEnabled"
          :is-translation-disabled="isTranslationDisabled"
          :agent-language="agentLanguage"
          :client-language="clientLanguage"
          :message-text="messageText"
          :user-name="userName"
          :reply-to-message="replyToMessage"
          @send-message="$emit('send-message')"
          @update:message-text="messageText = $event"
          @start-audio-recording="$emit('start-audio-recording')"
          @toggle-signature="$emit('toggle-signature')"
          @toggle-translation="$emit('toggle-translation')"
          @update:agent-language="$emit('update:agent-language', $event)"
          @update:client-language="$emit('update:client-language', $event)"
          @open-camera="$emit('open-camera')"
          @files-select="(files, category) => $emit('files-select', files, category)"
          @cancel-reply="$emit('cancel-reply')"
          @end-chat="$emit('end-chat')"
          @transfer-chat="$emit('transfer-chat')"
        >
          <template #actions-right>
            <Button
              v-if="showTransferButton"
              text
              rounded
              severity="secondary"
              class="shrink-0 gap-1.5 px-2! sm:gap-2 dark:hover:!bg-[var(--p-primary-900)]/70"
              :title="`${t('whatsapp.actions.chat_transfer')} (Alt+Shift+T)`"
              :aria-label="`${t('whatsapp.actions.chat_transfer')} (Alt+Shift+T)`"
              v-tooltip.top="`${t('whatsapp.actions.chat_transfer')} (Alt+Shift+T)`"
              @click="$emit('transfer-chat')"
            >
              <IconTransfer width="16" height="16" class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]" />
              <span class="hidden text-sm text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)] sm:inline">
                {{ t('whatsapp.actions.chat_transfer') }}
              </span>
            </Button>
          </template>
        </MessageInput>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Button from 'primevue/button'
import { Check } from 'lucide-vue-next'
import type { Message } from '../../types/interfaces'
import type { FilePreviewCategory } from '../../composables/useFiles'
import { useChatDraftStore } from '../../stores/useChatDraftStore'
import AudioRecorder from '../../atoms/audio/AudioRecorder.vue'
import MessageInput from './MessageInput.vue'
import IconTransfer from '@/assets/svg/transfer_icon.svg?component'
import IconTemplate from '@/assets/svg/template_icon.svg?component'
import IconHand from '@/assets/svg/hand_wave_icon.svg?component'
import { useI18n } from 'vue-i18n'

interface Props {
  chatId?: string
  isPendingChat: boolean
  isTakingChat?: boolean
  showAudioRecorder: boolean
  isSignatureEnabled: boolean
  isTranslationEnabled?: boolean
  isTranslationDisabled?: boolean
  agentLanguage?: string
  clientLanguage?: string
  userName: string
  replyToMessage?: Message | null
  isOutside24HourWindow?: boolean
  isAwaitingClientReply?: boolean
  showTransferButton?: boolean
  isClosedByTimeout?: boolean
  timeoutCampaignName?: string | null
  timeoutBotId?: string | null
  releasedByTemplateReason?: string | null
}

const props = defineProps<Props>()
const { t } = useI18n()

// El close_reason de CHAT_RELEASED_BY_BULK_TEMPLATE distingue por qué se
// liberó el acceso: reactivación real de un chat EXPIRE (ventana vencida) vs.
// toma de un chat TEMPLATE ya reservado (sin ventana vencida), por masivo o
// individual. BULK_TEMPLATE_REACTIVATION es el fallback para valores
// desconocidos, ya que era el único motivo antes de separar los flujos.
const releaseBannerText = computed(() => {
  switch (props.releasedByTemplateReason) {
    case 'BULK_TEMPLATE_TAKEOVER':
      return t('whatsapp.actions.bulk_template_takeover_released')
    case 'TEMPLATE_TAKEOVER':
      return t('whatsapp.actions.template_takeover_released')
    case 'TEMPLATE_HANDOFF':
      return t('whatsapp.actions.template_handoff_released')
    default:
      return t('whatsapp.actions.bulk_template_reactivated')
  }
})
const emit = defineEmits<{
  'send-message': []
  'send-audio': [data: { blob: Blob; duration: number }]
  'cancel-audio': []
  'start-audio-recording': []
  'take-chat': []
  'toggle-signature': []
  'toggle-translation': []
  'update:agent-language': [language: string]
  'update:client-language': [language: string]
  'files-select': [files: File[], category?: FilePreviewCategory]
  'open-camera': []
  'cancel-reply': []
  'send-template': []
  'transfer-chat': []
  'end-chat': []
  'close-chat': []
  'close-bulk-template-chat': []
}>()

const messageInputRef = ref()
const draftStore = useChatDraftStore()

// Sin `chatId` (no deberia pasar montado dentro de un chat) el texto se queda
// en el componente, como antes.
const detachedMessageText = ref('')

// El texto del editor es el borrador del chat abierto: cambiar de chat trae el
// suyo y deja el anterior guardado, en vez de arrastrar uno solo entre todos.
const messageText = computed<string>({
  get: () => (props.chatId ? draftStore.getDraft(props.chatId) : detachedMessageText.value),
  set: (value) => {
    if (props.chatId) draftStore.setDraft(props.chatId, value)
    else detachedMessageText.value = value
  },
})

const handleTakeChat = () => {
  if (props.isTakingChat) return
  emit('take-chat')
}

const focusMessageInput = () => {
  messageInputRef.value?.focusInput?.()
}

defineExpose({
  messageInputRef,
  focusMessageInput,
  messageText,
})
</script>
